/**
 * Sage (admin) multi-item claim page. Thin wrapper that wires the shared
 * MultiItemClaimEditor to the admin product search, the child claim's capture
 * metadata, and the register-and-claim mutation. Reached from the submit-claim
 * wizard's claim-type step when an umbrella type is chosen and the
 * `multi_item_claims` capability is on.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { AddressLookupProvider } from '@/shared/components/ui/address-lookup-context'
import { createAddressLookupApi } from '@/admin/hooks/use-lookups'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import {
  useCheckRegistrationClaimDuplicates,
  useCreateClaimMetadata,
  useMultiItemClaimTypes,
  useRegisterAndClaim,
  type PotentialDuplicateClaim,
} from '@/admin/hooks/use-claims'
import { PotentialDuplicateClaimDialog } from '@/admin/components/claims/potential-duplicate-claim-dialog'
import {
  useConsumerLookup,
  usePublishedProducts,
  useUploadFile,
} from '@/admin/hooks/use-products'
import {
  MultiItemClaimEditor,
  type MultiItemClaimSubmitPayload,
} from '@/shared/components/claims/multi-item-claim-editor'
import { type ResourceOption } from '@/shared/components/ui/resource-search-input'
import { toastApiError } from '@/shared/lib/api-error'

export function MultiItemClaimPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const umbrellaKey = params.get('type') || ''

  const { data: typeData } = useMultiItemClaimTypes()
  const umbType = useMemo(
    () => (typeData?.types ?? []).find((t) => t.key === umbrellaKey),
    [typeData, umbrellaKey],
  )
  const childKey = umbType?.child_claim_schema_key || ''

  // Browse the cached catalogue instead of gating on a 2-char search: the
  // picker paints the most-frequently-registered products on open (from the
  // persisted `published-products` cache), and the typed filter narrows via
  // the same list endpoint. Debounced so typing doesn't fire per keystroke.
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(handle)
  }, [query])
  const {
    data: productData,
    isLoading: productsLoading,
    isFetching: productsFetching,
  } = usePublishedProducts({
    registrable: true,
    ordering: '-registration_count',
    search: debouncedQuery,
  })
  const productResults: ResourceOption[] = useMemo(
    () =>
      (productData?.results ?? []).map((p) => ({
        uuid: p.uuid,
        name: p.cached_long_name || p.cached_short_name || p.name || 'Product',
        secondary:
          [p.product_template?.name, p.category?.name].filter(Boolean).join(' / ') ||
          undefined,
      })),
    [productData],
  )

  const { data: metadata, isLoading: fieldsLoading } = useCreateClaimMetadata(
    childKey || null,
  )

  // Address capture (DL-83): mount the admin address-lookup API for the
  // editor's `AddressInput`, and resolve the provider key off the consumer
  // entity's `address` attribute (same source the registration wizard uses).
  const queryClient = useQueryClient()
  const addressLookupApi = useMemo(
    () => createAddressLookupApi(queryClient),
    [queryClient],
  )
  const { data: consumerEntity } = useEntityTypeByKey('consumer')
  const addressLookupProvider = useMemo(
    () =>
      (consumerEntity?.attributes?.find(
        (a) => a.code_name === 'address' && a.field_type === 'ADDRESS',
      )?.configuration_data?.lookup_provider as string | undefined) ?? undefined,
    [consumerEntity],
  )

  // Debounce the email so the consumer lookup fires once the user pauses, not
  // on every keystroke.
  const [customerEmail, setCustomerEmail] = useState('')
  const [debouncedEmail, setDebouncedEmail] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmail(customerEmail), 400)
    return () => clearTimeout(timer)
  }, [customerEmail])
  const { data: existingCustomer } = useConsumerLookup(debouncedEmail)

  const register = useRegisterAndClaim()

  // WPS-150: registration-time potential-duplicate pre-check. This flow creates
  // the holding server-side, so it matches on the registration fields (consumer
  // + products) rather than a holding uuid. A match opens the shared warning
  // pop-up; "Continue" re-files with the held payload, skipping the gate.
  const checkDuplicates = useCheckRegistrationClaimDuplicates()
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [duplicateMatches, setDuplicateMatches] = useState<
    PotentialDuplicateClaim[]
  >([])
  const pendingPayloadRef = useRef<MultiItemClaimSubmitPayload | null>(null)

  const uploadFile = useUploadFile()
  const handleFileUpload = useCallback(
    async (file: File) => {
      const uploaded = await uploadFile.mutateAsync({
        file,
        use_case: 'CLAIM_EVIDENCE',
      })
      return {
        uuid: uploaded.uuid,
        file_name: uploaded.file_name,
        file_url: uploaded.file_url,
      }
    },
    [uploadFile],
  )

  const doRegister = useCallback(
    async (payload: MultiItemClaimSubmitPayload) => {
      if (!payload.consumer) return
      try {
        const result = await register.mutateAsync({
          claim_schema_key: umbrellaKey,
          consumer: {
            email: payload.consumer.email,
            first_name: payload.consumer.first_name,
            last_name: payload.consumer.last_name,
            phone: payload.consumer.phone,
            address: payload.consumer.address,
          },
          lines: payload.lines,
          shared_attribute_values: payload.shared_attribute_values,
        })
        toast.success(`Filed ${result.created} claim(s).`)
        navigate(
          result.parent_uuid
            ? `/claims/${result.parent_uuid}?tab=overview`
            : '/claims',
        )
      } catch (err) {
        toastApiError(err, 'Failed to file claim')
      }
    },
    [register, umbrellaKey, navigate],
  )

  const handleSubmit = async (payload: MultiItemClaimSubmitPayload) => {
    if (!payload.consumer) return
    // Pre-check for potential duplicates before filing. Best-effort - a failed
    // check never blocks the claim.
    try {
      const matches = await checkDuplicates.mutateAsync({
        consumer: {
          email: payload.consumer.email,
          first_name: payload.consumer.first_name,
          last_name: payload.consumer.last_name,
          phone: payload.consumer.phone,
          address: payload.consumer.address,
        },
        productUuids: payload.lines
          .map((line) => line.product_uuid)
          .filter(Boolean),
      })
      if (matches.length > 0) {
        pendingPayloadRef.current = payload
        setDuplicateMatches(matches)
        setDuplicateOpen(true)
        return
      }
    } catch {
      // Ignore - proceed to file.
    }
    await doRegister(payload)
  }

  const handleContinueDespiteDuplicates = () => {
    setDuplicateOpen(false)
    const payload = pendingPayloadRef.current
    pendingPayloadRef.current = null
    if (payload) void doRegister(payload)
  }

  return (
    <AppShell>
      <div className="min-h-[calc(100vh-48px)] bg-[var(--bg)] py-8">
        <div className="page-container">
          <AddressLookupProvider api={addressLookupApi}>
            <MultiItemClaimEditor
              title={umbType?.label || 'Multi-item claim'}
              description="Register several items and file a claim for each, under one claim."
              captureCustomer
              existingCustomer={existingCustomer}
              onCustomerEmailChange={setCustomerEmail}
              claimFields={metadata?.fields ?? []}
              fieldsLoading={!!childKey && fieldsLoading}
              productResults={productResults}
              productsLoading={productsLoading}
              productsFetching={productsFetching}
              addressLookupProvider={addressLookupProvider}
              onProductSearch={setQuery}
              onSubmit={handleSubmit}
              onFileUpload={handleFileUpload}
              isSubmitting={register.isPending || checkDuplicates.isPending}
              submitLabel="Submit claim"
              onCancel={() => navigate('/claims')}
            />
          </AddressLookupProvider>
        </div>
      </div>
      <PotentialDuplicateClaimDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        matches={duplicateMatches}
        onContinue={handleContinueDespiteDuplicates}
        isContinuing={register.isPending}
      />
    </AppShell>
  )
}
